// src/theme/minimalTheme.js
// ─────────────────────────────────────────────────────────────────────────
// JS mirror of the design tokens in src/ui/tokens.css, for places that need a
// literal colour (Chart.js / Recharts / canvas) instead of a CSS variable.
// Dark cool ground, ONE orange accent used sparingly, thin marks, muted
// neutrals. Keep the values in sync with tokens.css.
// ─────────────────────────────────────────────────────────────────────────

export const colors = {
  bg: "#0a0c10",
  bgElev: "#0e1117",
  surface: "#13171e",
  surface2: "#181d26",
  surface3: "#1f2530",
  panel: "#13171e",
  ink: "#e9ebef",
  ink2: "#a8aebb",
  dim: "#a8aebb",
  faint: "#6f7685",
  line: "rgba(255,255,255,0.07)",
  line2: "rgba(255,255,255,0.045)",
  grid: "rgba(255,255,255,0.06)",
  accent: "#ff6b1a", // the single accent — "us" / highlighted value
  accentInk: "#ffb68c",
  track: "rgba(255,255,255,0.07)",
  barMute: "rgba(255,255,255,0.18)", // opponent / non-highlighted marks
  // status — ALWAYS ship with a text label (never colour alone)
  win: "#2fbf71",
  lose: "#ef4d5a",
  warn: "#f5a524",
  info: "#4aa3ff",
  blueSide: "#4a8dff",
  redSide: "#ff5a5f",
};

// Categorical chart slots, fixed order (validated for colour-blind separation on the dark surface).
export const series = ["#3987e5", "#e8692a", "#199e70", "#c98500", "#d55181", "#9085e9"];

export const fonts = {
  sans: "'IBM Plex Sans Thai', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  display: "'Chakra Petch', 'IBM Plex Sans Thai', system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
};

export const space = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };
export const radius = { sm: 8, md: 12, lg: 16 };

// Recharts primitives — spread onto <CartesianGrid>, tick={...}, <Legend wrapperStyle>, <Tooltip contentStyle>.
export const chart = {
  grid: { stroke: colors.grid },
  axisTick: { fill: colors.faint, fontSize: 11, fontWeight: 500, fontFamily: fonts.sans },
  legend: { color: colors.ink2, fontSize: 12, fontFamily: fonts.sans, paddingTop: 10 },
  tooltip: {
    background: colors.surface2,
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    color: colors.ink,
    fontSize: 12,
    fontFamily: fonts.sans,
    boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
  },
  // series roles
  us: colors.accent,
  opp: colors.barMute,
};

// Chart.js: call once at app init so every react-chartjs-2 surface shares font, ink, grid and tooltip.
export function applyChartJsDefaults(ChartJS) {
  if (!ChartJS || !ChartJS.defaults) return;
  const d = ChartJS.defaults;
  d.color = colors.ink2;
  d.borderColor = colors.grid;
  if (d.font) {
    d.font.family = fonts.sans;
    d.font.size = 12;
  }
  const tt = d.plugins?.tooltip;
  if (tt) {
    tt.backgroundColor = colors.surface2;
    tt.borderColor = "rgba(255,255,255,0.12)";
    tt.borderWidth = 1;
    tt.titleColor = colors.ink;
    tt.bodyColor = colors.ink2;
    tt.padding = 10;
    tt.cornerRadius = 8;
    tt.boxPadding = 4;
    tt.titleFont = { family: fonts.sans, weight: "600", size: 12.5 };
    tt.bodyFont = { family: fonts.sans, size: 12 };
  }
  const lg = d.plugins?.legend?.labels;
  if (lg) {
    lg.color = colors.ink2;
    lg.usePointStyle = true;
    lg.boxWidth = 8;
    lg.boxHeight = 8;
  }
}

const minimalTheme = { colors, series, fonts, space, radius, chart, applyChartJsDefaults };
export default minimalTheme;
